// src/crm/templates/TemplatesPage.tsx
import { useState } from 'react'
import type { ContactType, Creator, MessageChannel, MessageTemplate, TemplateLanguage } from '../../lib/types'
import { useAppStore } from '../../shell/appStore'
import {
  useMessageTemplates,
  useCreateMessageTemplate,
  useUpdateMessageTemplate,
  useDeleteMessageTemplate,
  type CreateTemplateInput,
} from './useMessageTemplates'
import { resolveVariables } from './resolve'
import { CONTACT_TYPE_LABELS } from '../components/ContactTypeBadge'

const CHANNEL_LABELS: Record<MessageChannel, string> = {
  email: 'Email',
  whatsapp: 'WhatsApp',
  instagram_dm: 'DM Instagram',
  tiktok_dm: 'DM TikTok',
  call: 'Appel',
  note: 'Note',
  other: 'Autre',
}

// Contact fictif pour l'aperçu de résolution
const EXAMPLE_CONTACT: Creator = {
  id: 'exemple',
  displayName: 'Marie Dupont',
  collaborationType: 'audience',
  contactType: 'ugc_creator',
  organization: 'Exemple Média',
  contactRole: 'Journaliste',
  niche: 'musculation',
  audienceCountry: 'FR',
  audienceLanguage: 'fr',
  defaultRate: 200,
  currency: 'EUR',
  email: 'marie@example.com',
  notes: null,
  source: 'manual',
  socials: [
    {
      id: 'ex-s1', creatorId: 'exemple', platform: 'instagram', handle: '@marie',
      profileUrl: null, followers: 45000, engagementRate: null,
      updatedAt: '2026-01-01T00:00:00.000Z',
    },
  ],
  createdAt: '2026-01-01T00:00:00.000Z',
  updatedAt: '2026-01-01T00:00:00.000Z',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: 'var(--elevated)',
  border: '1px solid var(--line-strong)',
  borderRadius: '7px',
  padding: '0.5rem 0.75rem',
  color: 'var(--fg)',
  fontSize: '0.875rem',
  outline: 'none',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: 'var(--muted)',
  marginBottom: '0.375rem',
}

type FormState = {
  name: string
  channel: MessageChannel
  contactType: ContactType | ''
  language: TemplateLanguage
  subject: string
  body: string
}

const DEFAULT_FORM: FormState = {
  name: '',
  channel: 'email',
  contactType: '',
  language: 'fr',
  subject: '',
  body: '',
}

export function TemplatesPage() {
  const { activeAppId } = useAppStore()
  const { data: templates = [], isLoading, isError } = useMessageTemplates(activeAppId)
  const createTemplate = useCreateMessageTemplate()
  const deleteTemplate = useDeleteMessageTemplate()

  const [editingId, setEditingId] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState<FormState>(DEFAULT_FORM)
  const updateTemplate = useUpdateMessageTemplate(editingId ?? '')

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function startEdit(tpl: MessageTemplate) {
    setEditingId(tpl.id)
    setForm({
      name: tpl.name,
      channel: tpl.channel,
      contactType: tpl.contactType ?? '',
      language: tpl.language,
      subject: tpl.subject ?? '',
      body: tpl.body,
    })
    setShowForm(true)
  }

  function startCreate() {
    setEditingId(null)
    setForm(DEFAULT_FORM)
    setShowForm(true)
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!activeAppId || !form.name.trim() || !form.body.trim()) return
    const payload: CreateTemplateInput = {
      appId: activeAppId,
      name: form.name.trim(),
      channel: form.channel,
      contactType: form.contactType || null,
      language: form.language,
      subject: form.channel === 'email' ? form.subject.trim() || null : null,
      body: form.body,
    }
    if (editingId) {
      const { appId: _appId, ...updateBody } = payload
      await updateTemplate.mutateAsync(updateBody)
    } else {
      await createTemplate.mutateAsync(payload)
    }
    setShowForm(false)
    setEditingId(null)
    setForm(DEFAULT_FORM)
  }

  const preview = resolveVariables(form.body, {
    contact: EXAMPLE_CONTACT,
    deal: null,
    appName: 'Basalt Lab',
  })

  return (
    <div style={{ padding: '2rem 2.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--fg)', margin: 0, letterSpacing: '-0.025em' }}>
          Templates
        </h1>
        <button
          onClick={startCreate}
          style={{
            background: 'var(--arancio)', color: '#fff', border: 'none', borderRadius: '7px',
            padding: '0.4375rem 0.875rem', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer',
          }}
        >
          + Nouveau template
        </button>
      </div>

      {isLoading && <p style={{ color: 'var(--muted)' }}>Chargement…</p>}
      {isError && <p style={{ color: 'var(--arancio)' }}>Erreur lors du chargement des templates.</p>}

      {showForm && (
        <form
          onSubmit={(e) => { void handleSubmit(e) }}
          style={{
            background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: '10px',
            padding: '1.25rem', marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem',
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={labelStyle} htmlFor="tpl-name">Nom *</label>
              <input id="tpl-name" type="text" required value={form.name}
                onChange={(e) => set('name', e.target.value)} style={inputStyle} placeholder="Pitch presse FR" />
            </div>
            <div>
              <label style={labelStyle} htmlFor="tpl-channel">Canal</label>
              <select id="tpl-channel" value={form.channel}
                onChange={(e) => set('channel', e.target.value as MessageChannel)} style={inputStyle}>
                {Object.entries(CHANNEL_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle} htmlFor="tpl-type">Type de contact</label>
              <select id="tpl-type" value={form.contactType}
                onChange={(e) => set('contactType', e.target.value as ContactType | '')} style={inputStyle}>
                <option value="">Tous</option>
                {Object.entries(CONTACT_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle} htmlFor="tpl-lang">Langue</label>
              <select id="tpl-lang" value={form.language}
                onChange={(e) => set('language', e.target.value as TemplateLanguage)} style={inputStyle}>
                <option value="fr">FR</option>
                <option value="en">EN</option>
              </select>
            </div>
          </div>

          {form.channel === 'email' && (
            <div>
              <label style={labelStyle} htmlFor="tpl-subject">Sujet</label>
              <input id="tpl-subject" type="text" value={form.subject}
                onChange={(e) => set('subject', e.target.value)} style={inputStyle}
                placeholder="[APP IOS] {app} — …" />
            </div>
          )}

          <div>
            <label style={labelStyle} htmlFor="tpl-body">
              Corps * — variables : {'{prenom} {organisation} {reseau} {followers} {app} {code_promo} {tarif}'}
            </label>
            <textarea id="tpl-body" required rows={8} value={form.body}
              onChange={(e) => set('body', e.target.value)}
              style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5, fontFamily: 'inherit' }} />
          </div>

          {form.body && (
            <div style={{
              background: 'var(--elevated)', border: '1px dashed var(--line-strong)', borderRadius: '7px',
              padding: '0.75rem', fontSize: '0.8125rem', color: 'var(--muted)', whiteSpace: 'pre-wrap',
            }}>
              <p style={{ margin: '0 0 0.5rem', fontWeight: 600, color: 'var(--dim)', textTransform: 'uppercase', fontSize: '0.6875rem', letterSpacing: '0.04em' }}>
                Aperçu (contact d'exemple)
              </p>
              {preview.text}
              {preview.missing.length > 0 && (
                <p style={{ margin: '0.5rem 0 0', color: 'var(--arancio)' }}>
                  Non résolu sur l'exemple : {preview.missing.map((m) => `{${m}}`).join(', ')}
                </p>
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button type="button" onClick={() => { setShowForm(false); setEditingId(null) }}
              style={{ background: 'var(--elevated)', border: '1px solid var(--line)', borderRadius: '7px', padding: '0.5rem 1rem', color: 'var(--muted)', fontSize: '0.875rem', cursor: 'pointer' }}>
              Annuler
            </button>
            <button type="submit"
              style={{ background: 'var(--arancio)', border: 'none', borderRadius: '7px', padding: '0.5rem 1.25rem', color: '#fff', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer' }}>
              {editingId ? 'Mettre à jour' : 'Créer'}
            </button>
          </div>
        </form>
      )}

      {!isLoading && !isError && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {templates.length === 0 && !showForm && (
            <p style={{ color: 'var(--muted)' }}>
              Aucun template. Créez le premier avec « + Nouveau template ».
            </p>
          )}
          {templates.map((tpl) => (
            <div key={tpl.id} style={{
              background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: '10px',
              padding: '0.875rem 1rem', display: 'flex', alignItems: 'center', gap: '0.75rem',
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, color: 'var(--fg)', fontWeight: 500, fontSize: '0.875rem' }}>{tpl.name}</p>
                <p style={{ margin: '0.25rem 0 0', color: 'var(--dim)', fontSize: '0.75rem' }}>
                  {CHANNEL_LABELS[tpl.channel]} · {tpl.contactType ? CONTACT_TYPE_LABELS[tpl.contactType] : 'Tous types'} · {tpl.language.toUpperCase()}
                </p>
              </div>
              <button onClick={() => startEdit(tpl)}
                style={{ background: 'var(--elevated)', border: '1px solid var(--line)', borderRadius: '5px', padding: '0.25rem 0.5rem', color: 'var(--muted)', fontSize: '0.75rem', cursor: 'pointer' }}>
                Modifier
              </button>
              <button
                onClick={() => {
                  if (confirm(`Supprimer le template "${tpl.name}" ?`)) deleteTemplate.mutate(tpl.id)
                }}
                style={{ background: 'rgba(217,83,30,0.12)', border: '1px solid rgba(217,83,30,0.25)', borderRadius: '5px', padding: '0.25rem 0.5rem', color: 'var(--arancio)', fontSize: '0.75rem', cursor: 'pointer' }}>
                Supprimer
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
